import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FaSignOutAlt } from "react-icons/fa";
import { FiChevronDown } from "react-icons/fi";
import { fontFamily } from "../../shared/styles/theme";
import { useDispatch } from "react-redux";
import { logout } from "../../../redux/reducer/authReducer";
import { Link, useLocation } from "react-router-dom";
import { BASE_URL } from "../../../helper/base-url";
import { getHomeDashboardPath } from "../../../utils/dashboardRoutes";
import { isActiveVerifiedUser } from "../../../utils/vettingWorkflow";
import { hasSessionToken } from "../../../utils/authSession";

function readUserData() {
  try {
    return JSON.parse(localStorage.getItem("userData") || "null");
  } catch {
    return null;
  }
}

const UserMenu = () => {
  const [sessionTick, setSessionTick] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    const onUp = () => setSessionTick((n) => n + 1);
    window.addEventListener("liber-user-updated", onUp);
    window.addEventListener("storage", onUp);
    return () => {
      window.removeEventListener("liber-user-updated", onUp);
      window.removeEventListener("storage", onUp);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  void sessionTick;
  const loggedInUser = hasSessionToken(readUserData()) ? readUserData() : null;
  if (!loggedInUser?.user) return null;

  const closeMenu = () => setOpen(false);
  const userType = String(loggedInUser.user.user_type || "").toLowerCase();
  const isAdmin = userType === "admin";
  const isBusiness = userType === "business";
  const isFreelancer = userType === "freelancers" || userType === "freelancer";
  const isVerified = isActiveVerifiedUser(loggedInUser.user);

  const myProjectsPathActive =
    location.pathname === "/my-projects" ||
    location.pathname.startsWith("/my-projects/");
  const businessProjectsActive =
    location.pathname === "/projects" ||
    location.pathname.startsWith("/projects/") ||
    location.pathname === "/view-project" ||
    location.pathname.startsWith("/view-project/");

  const firstName = loggedInUser.user.first_name || "";
  const lastName = loggedInUser.user.last_name || "";
  const displayName = `${firstName} ${lastName}`.trim() || "User";
  const dashboardPath = getHomeDashboardPath(loggedInUser.user);

  const rawPhoto = loggedInUser.user.photo;
  const hasPhoto = rawPhoto != null && String(rawPhoto).trim() !== "";
  const avatarSrc = hasPhoto ? `${BASE_URL}uploads/${rawPhoto}` : "";

  const initials = (() => {
    const a = firstName.trim().charAt(0);
    const b = lastName.trim().charAt(0);
    return `${a}${b}`.toUpperCase() || "U";
  })();

  return (
    <Wrapper ref={wrapRef}>
      <Toggle
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${displayName} account menu`}
        onClick={() => setOpen((v) => !v)}
      >
        <AvatarWrap>
          {hasPhoto ? (
            <AvatarImg src={avatarSrc} alt="" />
          ) : (
            <AvatarInitials aria-hidden="true">{initials}</AvatarInitials>
          )}
        </AvatarWrap>
        <NameText>{displayName}</NameText>
        <Chevron aria-hidden>
          <FiChevronDown size={16} />
        </Chevron>
      </Toggle>

      {open && (
        <Dropdown role="menu" aria-label="Account">
          {isAdmin ? (
            <>
              {/* Admin-only: valid /admin/* routes (marketplace Profile/Dashboard/etc. stay hidden) */}
              <MenuLink
                to="/admin"
                $active={location.pathname === "/admin"}
                onClick={closeMenu}
              >
                Admin Dashboard
              </MenuLink>
              <MenuLink
                to="/admin/subscribers"
                $active={location.pathname.startsWith("/admin/subscribers")}
                onClick={closeMenu}
              >
                Subscriptions
              </MenuLink>
              <MenuLink
                to="/admin/messages"
                $active={location.pathname.startsWith("/admin/messages")}
                onClick={closeMenu}
              >
                Messages
              </MenuLink>
              <MenuLink
                to="/admin/skills"
                $active={location.pathname.startsWith("/admin/skills")}
                onClick={closeMenu}
              >
                Skills Management
              </MenuLink>
              <MenuLink
                to="/admin/settings"
                $active={location.pathname.startsWith("/admin/settings")}
                onClick={closeMenu}
              >
                Settings
              </MenuLink>
            </>
          ) : (
            <>
              <MenuLink to={dashboardPath} onClick={closeMenu}>
                Dashboard
              </MenuLink>
              <MenuLink to="/profile" onClick={closeMenu}>
                Profile
              </MenuLink>

              <MenuLink to="/messages" onClick={closeMenu}>
                Messages
              </MenuLink>

              {/* Freelancer money / payout */}
              {isFreelancer && (
                <>
                  <MenuLink to="/payments" onClick={closeMenu}>
                    Payments &amp; payouts
                  </MenuLink>
                  <MenuLink to="/stripe-onboard" onClick={closeMenu}>
                    Connect payout account
                  </MenuLink>
                </>
              )}

              {/* Business payments */}
              {isBusiness && (
                <MenuLink to="/track-payments" onClick={closeMenu}>
                  Payments &amp; payouts
                </MenuLink>
              )}

              {isBusiness && (
                <>
                  <MenuLink
                    to="/projects"
                    $active={businessProjectsActive}
                    onClick={closeMenu}
                  >
                    My Projects
                  </MenuLink>
                  <MenuLink to="/project-proposals" onClick={closeMenu}>
                    Project proposals
                  </MenuLink>
                </>
              )}

              {isFreelancer && (
                <MenuLink
                  to="/my-projects"
                  $active={myProjectsPathActive}
                  onClick={closeMenu}
                >
                  My Projects
                </MenuLink>
              )}

              {isFreelancer && (
                <MenuLink
                  to="/vetting"
                  $active={
                    location.pathname === "/vetting" ||
                    location.pathname.startsWith("/vetting/")
                  }
                  onClick={closeMenu}
                >
                  {isVerified ? "Verified status" : "Get verified"}
                </MenuLink>
              )}

              {isFreelancer && (
                <MenuLink to="/job-listings" onClick={closeMenu}>
                  Job Listings
                </MenuLink>
              )}

              {isBusiness && (
                <MenuLink to="/jobs" onClick={closeMenu}>
                  Job Listings
                </MenuLink>
              )}

              {isFreelancer && (
                <MenuLink to="/job-applications" onClick={closeMenu}>
                  Job applications
                </MenuLink>
              )}

              <MenuLink to="/subscription-settings" onClick={closeMenu}>
                Subscription settings
              </MenuLink>
            </>
          )}

          <MenuButton
            type="button"
            $danger
            onClick={() => {
              closeMenu();
              dispatch(logout());
            }}
          >
            <FaSignOutAlt style={{ color: "#d90000" }} />
            Sign out
          </MenuButton>
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default UserMenu;

const Wrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  z-index: 10050;
`;

const AvatarWrap = styled.div`
  box-sizing: border-box;
  width: 36px;
  height: 36px;
  min-width: 36px;
  min-height: 36px;
  aspect-ratio: 1;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: #eaf2f6;
  border: 1px solid #d4d4d4;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const AvatarInitials = styled.span`
  font-family: ${fontFamily.font};
  font-weight: 700;
  font-size: 12px;
  color: #1d1e2b;
`;

const NameText = styled.span`
  font-family: ${fontFamily.font};
  font-size: 16px;
  font-weight: 600;
  color: #1d1e2b;
  white-space: nowrap;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Chevron = styled.span`
  display: inline-flex;
  align-items: center;
  color: #1d1e2b;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 2px;
  margin: 0;
  border: 0;
  background: transparent;
  border-radius: 999px;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(17, 138, 178, 0.08);
  }

  &:focus-visible {
    outline: 2px solid #118ab2;
    outline-offset: 2px;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.14);
  border: 1px solid #e8eef3;
  padding: 8px 0;
  min-width: 240px;
  max-width: min(280px, calc(100vw - 24px));
  max-height: min(70vh, 520px);
  overflow-y: auto;
  z-index: 10060;
  -webkit-overflow-scrolling: touch;
`;

const menuItemStyles = `
  font-family: ${fontFamily.font};
  padding: 12px 16px;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
  text-decoration: none;
  border: 0;
  background: none;
  border-bottom: 1px solid #e0e6ed;
`;

const MenuLink = styled(Link)`
  ${menuItemStyles}
  color: #333;

  &:hover {
    background-color: ${({ $active }) => ($active ? "#e8eaed" : "#f5f5f5")};
  }

  ${({ $active }) =>
    $active &&
    `
    background-color: #f0f2f5;
    &:hover {
      background-color: #e8eaed;
    }
  `}

  &:last-of-type {
    border-bottom: 1px solid #e0e6ed;
  }
`;

const MenuButton = styled.button`
  ${menuItemStyles}
  color: ${({ $danger }) => ($danger ? "#d90000" : "#333")};
  font-weight: ${({ $danger }) => ($danger ? 700 : 400)};
  border-bottom: 0;

  &:hover {
    background-color: ${({ $danger }) => ($danger ? "#fff5f5" : "#f5f5f5")};
  }
`;
